import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Press-to-talk button (spec §1.2 U-TSU-2, AC-TSU-5).
 *
 * - Handles touch + mouse pointers uniformly via pointer events.
 * - Space bar also acts as PTT (keyboard hold = press, release = release).
 * - Short-press guard (<500ms) calls `onTooShort` without triggering state
 *   transitions — audio still streams, but the UI surfaces a TOO_SHORT toast.
 */
export function PTTButton({
  label = "PRESS · TALK",
  disabled = false,
  minHoldMs = 500,
  onPressStart,
  onPressEnd,
  onTooShort,
}: {
  label?: string;
  disabled?: boolean;
  minHoldMs?: number;
  onPressStart: () => void;
  onPressEnd: () => void;
  onTooShort?: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const pressedAtRef = useRef<number | null>(null);
  const activePointerRef = useRef<number | null>(null);

  const handleDown = useCallback(
    (pointerId: number | null) => {
      if (disabled) return;
      if (activePointerRef.current !== null) return; // ignore multi-touch
      activePointerRef.current = pointerId;
      pressedAtRef.current = performance.now();
      setPressed(true);
      onPressStart();
    },
    [disabled, onPressStart],
  );

  const handleUp = useCallback(
    (pointerId: number | null) => {
      if (activePointerRef.current !== pointerId && pointerId !== null) return;
      if (!pressed) return;
      activePointerRef.current = null;
      setPressed(false);
      onPressEnd();
      const startedAt = pressedAtRef.current;
      pressedAtRef.current = null;
      if (startedAt !== null) {
        const held = performance.now() - startedAt;
        if (held < minHoldMs) onTooShort?.();
      }
    },
    [pressed, minHoldMs, onPressEnd, onTooShort],
  );

  // Global pointer-up / cancel — release PTT even if the pointer drifts off
  // the button before being lifted.
  useEffect(() => {
    if (!pressed) return;
    const up = (ev: PointerEvent) => handleUp(ev.pointerId);
    const cancel = (ev: PointerEvent) => handleUp(ev.pointerId);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [pressed, handleUp]);

  // Space bar as PTT (avoid repeat).
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.code !== "Space" || ev.repeat) return;
      if (isTypingTarget(ev.target)) return;
      ev.preventDefault();
      handleDown(null);
    };
    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.code !== "Space") return;
      if (isTypingTarget(ev.target)) return;
      ev.preventDefault();
      handleUp(null);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [handleDown, handleUp]);

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        handleDown(e.pointerId);
      }}
      onContextMenu={(e) => e.preventDefault()}
      aria-pressed={pressed}
      aria-label={label}
      className={[
        "relative flex h-40 w-40 select-none items-center justify-center",
        "rounded-full font-mono text-[11px] tracking-[0.22em]",
        "transition-transform active:scale-[0.98]",
        disabled
          ? "bg-rust/30 text-ink/60"
          : pressed
            ? "bg-rust-deep text-cream shadow-inner"
            : "bg-rust text-cream shadow-md hover:bg-rust/90",
      ].join(" ")}
      style={{ touchAction: "none", WebkitTouchCallout: "none" }}
    >
      <span
        className={[
          "absolute left-1/2 top-3 h-1.5 w-1.5 -translate-x-1/2 rounded-full",
          pressed ? "bg-cream" : "bg-cream/50",
        ].join(" ")}
        aria-hidden
      />
      {pressed ? "TX · ON" : label}
    </button>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}
